import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';
import { apiFetch, logEvent } from '../api';

function Appeal() {
  const navigate = useNavigate();
  const [patientName, setPatientName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [requestedMedication, setRequestedMedication] = useState('');
  const [clinicalHistory, setClinicalHistory] = useState('');
  const [denialLetter, setDenialLetter] = useState('');
  const [denialExplanation, setDenialExplanation] = useState('');
  const [appealLetter, setAppealLetter] = useState('');
  const [appealFaxNumber, setAppealFaxNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [pdfUploaded, setPdfUploaded] = useState(false);
  const [ocrUsed, setOcrUsed] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [currentRecordId, setCurrentRecordId] = useState(null);
  const [saved, setSaved] = useState(false);
  const [attested, setAttested] = useState(false);
  const [copied, setCopied] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!patientName.trim()) newErrors.patientName = 'Patient name is required';
    if (!dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    if (!diagnosis.trim()) newErrors.diagnosis = 'Diagnosis is required';
    if (!requestedMedication.trim()) newErrors.requestedMedication = 'Requested medication is required';
    if (!clinicalHistory.trim()) newErrors.clinicalHistory = 'Patient clinical history is required';
    if (!denialLetter.trim()) newErrors.denialLetter = 'Denial letter is required';
    return newErrors;
  };

  const ALLOWED_TYPES = ['.pdf', '.png', '.jpg', '.jpeg', '.tif', '.tiff'];

  const uploadDocument = async (file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!ALLOWED_TYPES.some((ext) => name.endsWith(ext))) {
      alert('Please upload a PDF, PNG, JPG, or TIFF file.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('This file is larger than 10 MB. Please upload only the denial letter pages.');
      return;
    }
    setUploadingPdf(true);
    setPdfUploaded(false);
    setOcrUsed(false);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiFetch('/extract-pdf', {
        method: 'POST',
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || 'The file could not be read. Please paste the text manually.');
      }
      if (!data.text || !data.text.trim()) {
        throw new Error('No text was found in this file. Please paste the text manually.');
      }
      setDenialLetter(data.text);
      setOcrUsed(!!data.ocr_used);
      setUploadedFileName(file.name);
      setPdfUploaded(true);
      setErrors((prev) => ({ ...prev, denialLetter: undefined }));
    } catch (error) {
      alert(error.message || 'The file could not be read. Please paste the text manually.');
    }
    setUploadingPdf(false);
  };

  const handlePdfUpload = (e) => {
    uploadDocument(e.target.files[0]);
    e.target.value = '';
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploadingPdf) return;
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (uploadingPdf) return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 1) {
      alert('Please drop one file at a time.');
      return;
    }
    uploadDocument(e.dataTransfer.files && e.dataTransfer.files[0]);
  };

  const removeUpload = () => {
    setPdfUploaded(false);
    setOcrUsed(false);
    setUploadedFileName('');
    setDenialLetter('');
  };

  const saveToHistory = async (explanation, letter, faxNumber) => {
    try {
      const response = await apiFetch('/save-appeal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_name: patientName,
          date_of_birth: dateOfBirth,
          diagnosis: diagnosis,
          requested_medication: requestedMedication,
          denial_letter: denialLetter,
          denial_explanation: explanation,
          appeal_letter: letter,
          clinical_history: clinicalHistory,
          appeal_fax_number: faxNumber || null,
        }),
      });
      const data = await response.json();
      if (data.id) {
        setCurrentRecordId(data.id);
      }
    } catch (error) {
      console.log('Save error:', error);
    }
  };

  const handleGenerate = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setErrors({});
    setLoading(true);
    setSaved(false);
    setAttested(false);
    setCopied(false);
    setCurrentRecordId(null);
    try {
      const letterDate = new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
      const response = await apiFetch('/generate-appeal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          denial_letter: denialLetter,
          patient_name: patientName,
          diagnosis: diagnosis,
          requested_medication: requestedMedication,
          clinical_history: clinicalHistory,
          letter_date: letterDate,
        }),
      });
      if (!response.ok) throw new Error('Generation failed');
      const data = await response.json();
      setDenialExplanation(data.denial_explanation);
      setAppealLetter(data.appeal_letter);
      setAppealFaxNumber(data.appeal_fax_number || '');
      await saveToHistory(data.denial_explanation, data.appeal_letter, data.appeal_fax_number);
    } catch (error) {
      setDenialExplanation('Error generating appeal. Please try again.');
      setAppealLetter('');
      setAppealFaxNumber('');
    }
    setLoading(false);
  };

  const handleSaveChanges = async () => {
    if (!currentRecordId) return;
    try {
      await apiFetch(`/update-appeal/${currentRecordId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appeal_letter: appealLetter, appeal_fax_number: appealFaxNumber, status: 'pending' }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.log('Update error:', error);
    }
  };

  const handleCopy = () => {
    if (!attested) {
      alert('Please confirm that a licensed healthcare professional has reviewed this appeal before copying.');
      return;
    }
    navigator.clipboard.writeText(appealLetter);
    logEvent('copy_appeal', currentRecordId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleNewAppeal = () => {
    if (appealLetter && !window.confirm('Start a new appeal? This appeal is already saved in Past Appeals.')) {
      return;
    }
    setPatientName('');
    setDateOfBirth('');
    setDiagnosis('');
    setRequestedMedication('');
    setClinicalHistory('');
    setDenialLetter('');
    setDenialExplanation('');
    setAppealLetter('');
    setAppealFaxNumber('');
    setPdfUploaded(false);
    setOcrUsed(false);
    setUploadedFileName('');
    setCurrentRecordId(null);
    setSaved(false);
    setAttested(false);
    setCopied(false);
    setErrors({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogout = async () => {
    await logEvent('logout');
    await signOut();
    navigate('/login');
  };

  const inputStyle = (field) => ({
    width: '100%',
    padding: '10px',
    borderRadius: '6px',
    border: errors[field] ? '1px solid #e53e3e' : '1px solid #ddd',
    fontSize: '14px',
    boxSizing: 'border-box'
  });

  const errorText = (field) => errors[field] ? (
    <p style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px' }}>{errors[field]}</p>
  ) : null;

  return (
    <div style={{
      fontFamily: 'Arial, sans-serif',
      maxWidth: '900px',
      margin: '0 auto',
      padding: '20px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        borderBottom: '2px solid #1a365d',
        paddingBottom: '15px'
      }}>
        <h1 style={{ color: '#1a365d', margin: 0 }}>Clinix AI</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={handleNewAppeal}
            style={{
              padding: '8px 16px',
              backgroundColor: '#1a365d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            New Appeal
          </button>
          <button
            onClick={() => navigate('/appeal-history')}
            style={{
              padding: '8px 16px',
              backgroundColor: '#edf2f7',
              color: '#1a365d',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Past Appeals
          </button>
          <button
            onClick={handleLogout}
            style={{
              padding: '8px 16px',
              backgroundColor: '#e53e3e',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Logout
          </button>
        </div>
      </div>

      <h2 style={{ color: '#2d3748', marginBottom: '8px' }}>
        PA Denial Appeal Generator
      </h2>
      <p style={{ color: '#718096', marginBottom: '24px' }}>
        Upload or paste the denial letter, add patient history, and we'll generate a targeted appeal letter.
      </p>

      {Object.keys(errors).filter((k) => errors[k]).length > 0 && (
        <div style={{
          backgroundColor: '#fff5f5',
          border: '1px solid #fc8181',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px'
        }}>
          <p style={{ color: '#c53030', fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
            Please fill in all required fields:
          </p>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {Object.values(errors).filter(Boolean).map((error, index) => (
              <li key={index} style={{ color: '#e53e3e', fontSize: '13px', marginBottom: '4px' }}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '10px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <h3 style={{ color: '#1a365d', marginBottom: '16px' }}>Patient Information</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', color: '#4a5568' }}>Patient Name *</label>
            <input
              type="text"
              placeholder="John Smith"
              value={patientName}
              onChange={(e) => { setPatientName(e.target.value); setErrors((prev) => ({ ...prev, patientName: undefined })); }}
              style={inputStyle('patientName')}
            />
            {errorText('patientName')}
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', color: '#4a5568' }}>Date of Birth *</label>
            <input
              type="date"
              value={dateOfBirth}
              onChange={(e) => { setDateOfBirth(e.target.value); setErrors((prev) => ({ ...prev, dateOfBirth: undefined })); }}
              style={inputStyle('dateOfBirth')}
            />
            {errorText('dateOfBirth')}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', color: '#4a5568' }}>Diagnosis *</label>
            <input
              type="text"
              placeholder="e.g. Multifocal Motor Neuropathy (G61.82)"
              value={diagnosis}
              onChange={(e) => { setDiagnosis(e.target.value); setErrors((prev) => ({ ...prev, diagnosis: undefined })); }}
              style={inputStyle('diagnosis')}
            />
            {errorText('diagnosis')}
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', color: '#4a5568' }}>Requested Medication *</label>
            <input
              type="text"
              placeholder="e.g. Gamunex-C"
              value={requestedMedication}
              onChange={(e) => { setRequestedMedication(e.target.value); setErrors((prev) => ({ ...prev, requestedMedication: undefined })); }}
              style={inputStyle('requestedMedication')}
            />
            {errorText('requestedMedication')}
          </div>
        </div>
        <div style={{ marginTop: '16px' }}>
          <label style={{ display: 'block', marginBottom: '6px', color: '#4a5568' }}>Patient Clinical History *</label>
          <p style={{ color: '#718096', fontSize: '12px', marginBottom: '8px' }}>
            Include how long on medication, recent evaluation dates, objective scale scores, and any relevant clinical notes
          </p>
          <textarea
            placeholder="e.g. Patient has been on IVIG since 2021. Last evaluated January 29, 2026 showing continued improvement..."
            value={clinicalHistory}
            onChange={(e) => { setClinicalHistory(e.target.value); setErrors((prev) => ({ ...prev, clinicalHistory: undefined })); }}
            rows={5}
            style={{ ...inputStyle('clinicalHistory'), resize: 'vertical', fontFamily: 'Arial, sans-serif' }}
          />
          {errorText('clinicalHistory')}
        </div>
      </div>

      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '10px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <h3 style={{ color: '#1a365d', marginBottom: '8px' }}>Denial Letter *</h3>
        <div
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
          style={{
            border: dragActive
              ? '2px dashed #1a365d'
              : errors.denialLetter ? '2px dashed #e53e3e' : '2px dashed #cbd5e0',
            borderRadius: '8px',
            padding: '20px',
            textAlign: 'center',
            marginBottom: '16px',
            backgroundColor: dragActive ? '#ebf4ff' : '#f7fafc'
          }}
        >
          {uploadingPdf ? (
            <p style={{ color: '#1a365d', fontSize: '14px', margin: '8px 0' }}>
              Reading document... Scanned or faxed pages can take up to 30 seconds.
            </p>
          ) : pdfUploaded ? (
            <div>
              <p style={{ color: '#2f855a', fontSize: '14px', margin: '0 0 8px' }}>
                Loaded: <strong>{uploadedFileName}</strong>
              </p>
              <button
                onClick={removeUpload}
                style={{
                  padding: '4px 10px',
                  backgroundColor: 'transparent',
                  color: '#e53e3e',
                  border: '1px solid #e53e3e',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Remove File
              </button>
            </div>
          ) : (
            <>
              <p style={{ color: '#718096', marginBottom: '12px', fontSize: '14px' }}>
                Drag and drop the denial letter here, or
              </p>
              <label style={{ padding: '8px 20px', backgroundColor: '#1a365d', color: 'white', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}>
                Choose File
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff"
                  onChange={handlePdfUpload}
                  style={{ display: 'none' }}
                />
              </label>
              <p style={{ color: '#a0aec0', fontSize: '12px', margin: '12px 0 0' }}>
                PDF, PNG, JPG, or TIFF. Scanned and faxed letters are supported. Up to 10 MB.
              </p>
            </>
          )}
        </div>

        {pdfUploaded && ocrUsed && (
          <div style={{
            backgroundColor: '#fffbeb',
            border: '1px solid #f6e05e',
            borderRadius: '6px',
            padding: '10px 14px',
            marginBottom: '12px',
            fontSize: '13px',
            color: '#744210'
          }}>
            This letter was scanned, so its text was read automatically. Please compare it with the
            original, especially names, dates, ID numbers, and the fax number, and correct anything below before generating.
          </div>
        )}

        <p style={{ color: '#718096', fontSize: '13px', marginBottom: '8px', textAlign: 'center' }}>
          {pdfUploaded ? 'Text read from your file (you can edit it):' : '- or paste the text manually -'}
        </p>
        <textarea
          placeholder="Paste the full denial letter text here..."
          value={denialLetter}
          onChange={(e) => { setDenialLetter(e.target.value); setErrors((prev) => ({ ...prev, denialLetter: undefined })); }}
          rows={pdfUploaded ? 12 : 8}
          style={{ ...inputStyle('denialLetter'), resize: 'vertical', fontFamily: 'Arial, sans-serif' }}
        />
        {errorText('denialLetter')}
      </div>

      <button
        onClick={handleGenerate}
        disabled={loading}
        style={{
          width: '100%',
          padding: '14px',
          backgroundColor: loading ? '#a0aec0' : '#1a365d',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          fontSize: '16px',
          cursor: loading ? 'not-allowed' : 'pointer',
          marginBottom: '20px'
        }}
      >
        {loading ? 'Generating Appeal...' : 'Generate Appeal Letter'}
      </button>

      {denialExplanation && (
        <div style={{
          backgroundColor: '#fff5f5',
          border: '1px solid #fed7d7',
          padding: '24px',
          borderRadius: '10px',
          marginBottom: '20px'
        }}>
          <h3 style={{ color: '#c53030', marginBottom: '12px' }}>Why It Was Denied — Plain English</h3>
          <p style={{ color: '#2d3748', lineHeight: '1.8', whiteSpace: 'pre-wrap', margin: 0 }}>{denialExplanation}</p>
        </div>
      )}

      {appealLetter && (
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '10px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ color: '#1a365d', margin: 0 }}>Generated Appeal Letter</h3>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {saved && <span style={{ color: '#48bb78', fontSize: '13px' }}>Saved</span>}
              <button
                onClick={handleSaveChanges}
                style={{ padding: '8px 16px', backgroundColor: '#1a365d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
              >
                Save Changes
              </button>
              <button
                onClick={handleCopy}
                disabled={!attested}
                style={{
                  padding: '8px 16px',
                  backgroundColor: attested ? '#48bb78' : '#a0aec0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: attested ? 'pointer' : 'not-allowed',
                  minWidth: '70px'
                }}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '14px',
            padding: '12px',
            backgroundColor: '#f7fafc',
            borderRadius: '6px',
            border: '1px solid #e2e8f0'
          }}>
            <label htmlFor="faxNumber" style={{ fontSize: '14px', color: '#4a5568', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              Fax appeal to:
            </label>
            <input
              id="faxNumber"
              type="text"
              value={appealFaxNumber}
              onChange={(e) => setAppealFaxNumber(e.target.value)}
              placeholder="Not found in denial letter - enter the insurer's appeal fax number"
              maxLength={30}
              style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }}
            />
          </div>
          <p style={{ color: '#718096', fontSize: '12px', marginTop: '-8px', marginBottom: '14px' }}>
            Taken from the denial letter. Please double-check before faxing. Click Save Changes if you edit it.
          </p>
          <textarea
            value={appealLetter}
            onChange={(e) => setAppealLetter(e.target.value)}
            rows={16}
            style={{ width: '100%', padding: '14px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', lineHeight: '1.8', color: '#2d3748', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'Arial, sans-serif' }}
          />
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#f7fafc',
            borderRadius: '6px',
            border: '1px solid #e2e8f0'
          }}>
            <input
              type="checkbox"
              id="attestation"
              checked={attested}
              onChange={(e) => setAttested(e.target.checked)}
              style={{ marginTop: '2px', cursor: 'pointer', width: '16px', height: '16px' }}
            />
            <label htmlFor="attestation" style={{ fontSize: '13px', color: '#4a5568', cursor: 'pointer', lineHeight: '1.5' }}>
              I confirm that a licensed healthcare professional has reviewed and validated this appeal text before use.
            </label>
          </div>
          <button
            onClick={handleNewAppeal}
            style={{
              width: '100%',
              marginTop: '16px',
              padding: '12px',
              backgroundColor: 'white',
              color: '#1a365d',
              border: '1px solid #1a365d',
              borderRadius: '6px',
              fontSize: '15px',
              cursor: 'pointer'
            }}
          >
            Start a New Appeal
          </button>
        </div>
      )}
    </div>
  );
}

export default Appeal;